import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, type ApiResponse } from "@/api/client";
import { sortBySearchMatch } from "@ckgo2/shared";
import { FileText, Mail, Plus, Search, Edit2, Copy, Trash2, Loader2, Filter } from "lucide-react";
import { formatDate } from "@/lib/format";
import toast from "react-hot-toast";
import clsx from "clsx";
import ConfirmModal from "@/components/common/ConfirmModal";

interface Template {
  id: string;
  slug: string;
  name: string;
  description?: string;
  type: "email" | "pdf";
  category: string;
  typeLabel: string;
  categoryLabel: string;
  isSystem: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const TYPE_ICONS = {
  email: Mail,
  pdf: FileText,
};

const TYPE_COLORS = {
  email: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  pdf: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
};

const CATEGORY_COLORS: Record<string, string> = {
  newsletter: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  booking: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  payment: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
  contract: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  invoice: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  voucher: "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400",
  other: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
};

export default function TemplatesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [categoryFilter, setCategoryFilter] = useState<string>("");

  // Fetch templates
  const { data: templatesData, isLoading } = useQuery({
    queryKey: ["templates", typeFilter, categoryFilter],
    queryFn: () => {
      const params = new URLSearchParams();
      if (typeFilter) params.set("type", typeFilter);
      if (categoryFilter) params.set("category", categoryFilter);
      const query = params.toString();
      return api.get<ApiResponse<Template[]>>(`/api/admin/templates${query ? `?${query}` : ""}`);
    },
  });

  // Fetch categories
  const { data: categoriesData } = useQuery({
    queryKey: ["template-categories"],
    queryFn: () =>
      api.get<
        ApiResponse<{
          categories: Record<string, string>;
          types: Record<string, string>;
        }>
      >("/api/admin/templates/categories"),
  });

  const templates = templatesData?.data || [];
  const categories = categoriesData?.data?.categories || {};
  const types = categoriesData?.data?.types || {};

  // Hledání filtruje i řadí podle shody s názvem, shoda jen v popisu nebo kategorii jde za ní.
  const filteredTemplates = sortBySearchMatch(templates, search, {
    name: (tpl) => tpl.name,
    other: (tpl) => [tpl.description, tpl.categoryLabel],
    tieBreak: () => 0,
  });

  // Duplicate mutation
  const duplicateMutation = useMutation({
    mutationFn: (id: string) =>
      api.post<ApiResponse<Template>>(`/api/admin/templates/${id}/duplicate`),
    onSuccess: () => {
      toast.success(t("opsAdmin.templates.toastDuplicated"));
      queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
    onError: (error: Error) => {
      toast.error(error.message || t("opsAdmin.templates.toastDuplicateFailed"));
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/templates/${id}`),
    onSuccess: () => {
      toast.success(t("opsAdmin.templates.toastDeleted"));
      queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
    onError: (error: Error) => {
      toast.error(error.message || t("opsAdmin.templates.toastDeleteFailed"));
    },
  });

  const [confirmDelete, setConfirmDelete] = useState<{ id: string; name: string } | null>(null);
  const handleDelete = (template: Template) => {
    if (template.isSystem) {
      toast.error(t("opsAdmin.templates.systemCannotDelete"));
      return;
    }
    setConfirmDelete({ id: template.id, name: template.name });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("opsAdmin.templates.title")}
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            {t("opsAdmin.templates.subtitle")}
          </p>
        </div>
        <Link to="/templates/new" className="btn-primary">
          <Plus className="h-5 w-5" />
          {t("opsAdmin.templates.new")}
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder={t("opsAdmin.templates.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-10"
          />
        </div>

        {/* Type filter */}
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-gray-400" />
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="input w-40"
          >
            <option value="">{t("opsAdmin.templates.allTypes")}</option>
            {Object.entries(types).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {/* Category filter */}
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="input w-48"
        >
          <option value="">{t("opsAdmin.templates.allCategories")}</option>
          {Object.entries(categories).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {/* Templates grid */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="text-primary-500 h-8 w-8 animate-spin" />
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="card flex h-64 flex-col items-center justify-center">
          <FileText className="mb-4 h-12 w-12 text-gray-300" />
          <p className="text-gray-500">{t("opsAdmin.templates.empty")}</p>
          <Link to="/templates/new" className="btn-primary mt-4">
            <Plus className="h-5 w-5" />
            {t("opsAdmin.templates.createFirst")}
          </Link>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredTemplates.map((template) => {
            const TypeIcon = TYPE_ICONS[template.type];

            return (
              <div
                key={template.id}
                className={clsx(
                  "card group relative transition-shadow hover:shadow-lg",
                  !template.isActive && "opacity-60",
                )}
              >
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={clsx(
                        "flex h-10 w-10 items-center justify-center rounded-lg",
                        TYPE_COLORS[template.type],
                      )}
                    >
                      <TypeIcon className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {template.name}
                      </h3>
                      <div className="mt-1 flex items-center gap-2">
                        <span
                          className={clsx(
                            "inline-flex items-center rounded px-2 py-0.5 text-xs font-medium",
                            CATEGORY_COLORS[template.category] || CATEGORY_COLORS.other,
                          )}
                        >
                          {template.categoryLabel}
                        </span>
                        {template.isSystem && (
                          <span className="text-xs text-gray-500">
                            {t("opsAdmin.templates.systemChip")}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Status indicator */}
                  <div
                    className={clsx(
                      "h-2 w-2 rounded-full",
                      template.isActive ? "bg-success-500" : "bg-gray-400",
                    )}
                    title={
                      template.isActive ? t("common.status.active") : t("common.status.inactive")
                    }
                  />
                </div>

                {/* Description */}
                {template.description && (
                  <p className="mt-3 line-clamp-2 text-sm text-gray-600 dark:text-gray-400">
                    {template.description}
                  </p>
                )}

                {/* Footer */}
                <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4 dark:border-gray-700">
                  <span className="text-xs text-gray-500">
                    {t("opsAdmin.templates.updatedAt", { date: formatDate(template.updatedAt) })}
                  </span>

                  {/* Actions */}
                  <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                    <Link
                      to={`/templates/${template.id}`}
                      className="btn-ghost btn-sm"
                      title={t("common.actions.edit")}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Link>
                    <button
                      onClick={() => duplicateMutation.mutate(template.id)}
                      disabled={duplicateMutation.isPending}
                      className="btn-ghost btn-sm"
                      title={t("common.actions.duplicate")}
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                    {!template.isSystem && (
                      <button
                        onClick={() => handleDelete(template)}
                        disabled={deleteMutation.isPending}
                        className="btn-ghost btn-sm text-error-600 hover:bg-error-50"
                        title={t("common.actions.delete")}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => {
          if (confirmDelete) deleteMutation.mutate(confirmDelete.id);
          setConfirmDelete(null);
        }}
        isLoading={deleteMutation.isPending}
        title={t("opsAdmin.templates.deleteTitle")}
        message={
          confirmDelete ? t("opsAdmin.templates.deleteMessage", { name: confirmDelete.name }) : ""
        }
        confirmText={t("common.actions.delete")}
        variant="danger"
      />
    </div>
  );
}
